import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  openGalleraCall,
  answerGalleraInboundCall,
  uploadGalleraRecording,
  startRingtone,
  listMicrophones,
  voiceErrorMessage,
  ensureAudioUnlocked
} from "../../lib/galleraVoice";

const MIC_PREF_KEY = "gallera_voice_mic";

/**
 * gallera-voice FASE 2 — atender ligações de WhatsApp DENTRO do painel.
 *
 * Escuta o socket `company-<companyId>-call` (offer/answered/claimed/ended)
 * emitido pelo backend a partir do SSE do motor WaCalls. Suporta VÁRIAS chamadas
 * simultâneas: mantém uma LISTA de chamadas (não uma só), cada uma com seu
 * mini-card empilhado no canto — porque 1 número pode ter até 8 chamadas ao mesmo
 * tempo (max-calls-per-session=8) e o atendente pode segurar mais de uma.
 *
 * Ao chegar "offer" mostra um card com Atender/Recusar. Ao Atender: chama o
 * backend (/voice/:id/answer) e abre o WebRTC (DataChannel PCM + AudioWorklet,
 * portados do WaCalls) via o proxy /voice. "claimed" fecha o card se OUTRO
 * atendente pegou a chamada. Desligar/encerrar limpa tudo daquela chamada.
 *
 * Gated por plano (useVoice) + serviço configurado no servidor: só ativa o
 * listener se GET /voice/enabled responder enabled=true.
 *
 * UX espelha o WavoipCall/WebphoneFab (canto inferior direito, azul Gallera).
 */

const styles = {
  stack: {
    position: "fixed",
    right: 20,
    bottom: 90,
    zIndex: 13000,
    display: "flex",
    flexDirection: "column-reverse", // mais recentes embaixo, perto do FAB
    gap: 10,
    fontFamily: "Arial, sans-serif"
  },
  card: {
    width: 300,
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    overflow: "hidden"
  },
  header: {
    backgroundColor: "#00339E",
    color: "#fff",
    padding: 14,
    fontWeight: "bold",
    fontSize: 15
  },
  body: { padding: 18, textAlign: "center" },
  number: { fontSize: 18, fontWeight: "bold", marginBottom: 4 },
  sub: { fontSize: 13, color: "#666", marginBottom: 12 },
  duration: { fontSize: 14, color: "#333", marginBottom: 12 },
  actions: { display: "flex", gap: 10, padding: "0 18px 18px" },
  btn: {
    flex: 1,
    height: 46,
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: "bold",
    color: "#fff",
    cursor: "pointer"
  },
  answer: { backgroundColor: "#28a745" },
  reject: { backgroundColor: "#dc3545" },
  hangup: { backgroundColor: "#dc3545" },
  ctrl: { backgroundColor: "#6c757d", fontSize: 13 },
  ctrlOn: { backgroundColor: "#00339E", fontSize: 13 },
  recNotice: {
    fontSize: 11,
    color: "#c0392b",
    fontWeight: "bold",
    marginBottom: 8
  },
  micSelect: {
    marginTop: 10,
    width: "100%",
    padding: "6px 8px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 13
  }
};

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
};

/**
 * Card de UMA chamada. Isola o WebRTC/áudio/cronômetro por chamada — assim várias
 * chamadas coexistem sem interferência (cada uma com seu RTCPeerConnection e seu
 * elemento <audio>).
 */
const CallCard = ({ call, onRemove, record }) => {
  // phases: ringing (inbound aguardando Atender) | answering (inbound conectando a
  // mídia após o Atender) | dialing (outbound chamando...) | inCall
  const [phase, setPhase] = useState(call.phase || "ringing");
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [onHold, setOnHold] = useState(false);
  // WebRTC aberto (áudio de pé). Controla a exibição de Mudo/Espera no OUTBOUND:
  // no caminho por token o evento "answered" pode não chegar (a fase fica presa em
  // "dialing" mesmo com a conversa rolando) e os controles sumiam — com isso o
  // atendente ficava só com "Desligar". Mudo/Espera são 100% client-side (track do
  // mic + mute do alto-falante), então funcionam em QUALQUER caminho (token/legado).
  const [rtcReady, setRtcReady] = useState(false);
  const [mics, setMics] = useState([]);
  const [selectedMic, setSelectedMic] = useState(() => {
    try { return localStorage.getItem(MIC_PREF_KEY) || ""; } catch (_) { return ""; }
  });

  const audioRef = useRef(null);
  const rtcRef = useRef(null);
  const timerRef = useRef(null);
  const removedRef = useRef(false);
  const outboundStartedRef = useRef(false);
  const ringStopRef = useRef(null);
  // owner/clientId da chamada p/ webrtc/hangup. OUTBOUND: vem no call (gv-<user>-out-
  // <nonce>). INBOUND: só existe DEPOIS do /answer (gv-<user>-<callId>) — guardamos
  // aqui p/ o hangup usar o mesmo owner (simetria com o fix outbound-clientid).
  const clientIdRef = useRef(call.clientId || null);
  // EU atendi esta chamada (cliquei em Atender). Protege o card do "claimed" do
  // PRÓPRIO atendimento: no caminho por token o owner que o motor reporta NÃO é
  // gv-<user>-..., então o filtro por owner do overlay não reconhece o próprio
  // atendente — sem esta guarda o card era fechado e o WebRTC recém-negociado
  // era derrubado (chamada atendida ficava MUDA). Regressão 2026-07-06.
  const answeredByMeRef = useRef(false);
  const answeringRef = useRef(false); // anti duplo-clique no Atender
  // espelhos dos estados de controle p/ aplicar no rtc DEPOIS que a mídia abrir
  // (o usuário pode clicar Mudo/Espera na janela em que o WebRTC ainda negocia)
  const mutedRef = useRef(false);
  const onHoldRef = useRef(false);

  const teardown = useCallback(() => {
    if (ringStopRef.current) {
      try { ringStopRef.current(); } catch (_) {}
      ringStopRef.current = null;
    }
    if (rtcRef.current) {
      try { rtcRef.current.close(); } catch (_) {}
      rtcRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current.srcObject = null;
    }
  }, []);

  const finish = useCallback(() => {
    if (removedRef.current) return;
    removedRef.current = true;
    const rtc = rtcRef.current;
    // flush da gravação (best-effort) ANTES de derrubar o áudio; depois remove o card
    (async () => {
      try {
        if (record && rtc && rtc.stopRecording) {
          const blob = await rtc.stopRecording();
          // eslint-disable-next-line no-console
          console.log("[gravacao] record=true, blob bytes:", blob ? blob.size : 0);
          if (blob && blob.size > 0) {
            await uploadGalleraRecording(call.whatsappId, call.callId, blob);
            toast.success("Gravação salva ✓");
          } else {
            toast.warn("Gravação não capturou áudio (vazio)");
          }
        } else {
          // eslint-disable-next-line no-console
          console.log("[gravacao] desligada (record=" + record + ")");
        }
      } catch (e) {
        toast.error("Falha ao salvar gravação: " + (e && e.message ? e.message : e));
      }
      teardown();
      onRemove(call.callId);
    })();
  }, [teardown, onRemove, call.callId, call.whatsappId, record]);

  const handleHangup = useCallback(async () => {
    try {
      // clientId: no OUTBOUND o dono é gv-<user>-out-<nonce>; no INBOUND atendido é o
      // gv-<user>-<callId> devolvido pelo /answer (clientIdRef). Sem ele o motor não
      // casa o owner e a call fica de pé — ver [[project_voz_outbound_clientid_regressao]].
      await api.post(`/voice/${call.whatsappId}/hangup`, { callId: call.callId, clientId: clientIdRef.current });
    } catch (err) {
      toastError(err);
    }
    finish();
  }, [call.whatsappId, call.callId, finish]);

  // reage aos eventos externos anexados ao objeto call (answered/claimed/ended)
  useEffect(() => {
    if (call.signal === "ended") {
      finish();
    } else if (call.signal === "claimed") {
      // "claimed" = alguém atendeu. Se fui EU (answeredByMeRef), IGNORA: é o eco do
      // meu próprio atendimento — no caminho por token o owner não vem no formato
      // gv-<user>-..., o overlay não reconhece o próprio atendente e fechava o card,
      // derrubando o áudio recém-negociado (atendia e ficava mudo).
      if (!answeredByMeRef.current) finish();
    } else if (
      call.signal === "answered" &&
      (phase === "ringing" || phase === "dialing" || phase === "answering")
    ) {
      // inbound: eu (ou outro dispositivo MEU) atendi — vira "em chamada".
      // outbound: o contato atendeu e o motor conectou — sai de "chamando...".
      setPhase("inCall");
    }
  }, [call.signal, phase, finish]);

  // OUTBOUND: ao montar, o backend já originou a chamada (callId conhecido).
  // Abrimos o WebRTC direto (sem "Atender") e mostramos "chamando..." até o
  // socket "answered" chegar. NÃO desliga se o áudio abrir antes do atendimento —
  // o motor segura a mídia; a transição para "em chamada" vem do evento answered.
  useEffect(() => {
    if (call.direction !== "outbound" || outboundStartedRef.current) return;
    outboundStartedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const rtc = await openGalleraCall(
          call.whatsappId,
          call.callId,
          selectedMic || null,
          record,
          call.clientId || null,
          // FALLBACK "atendida": 1º áudio remoto → sai de "chamando…" mesmo que o
          // evento answered do socket não chegue (caminho por token). Se o motor
          // mandar áudio antes do atendimento real, o cronômetro começa cedo —
          // cosmético; melhor do que ringback tocando por cima da conversa.
          () => {
            setPhase((p) => {
              if (p === "dialing") {
                // eslint-disable-next-line no-console
                console.log("[GALLERA-VOICE] saída: áudio remoto chegou — assumindo chamada atendida (fallback sem evento answered)");
                return "inCall";
              }
              return p;
            });
          }
        );
        if (cancelled) {
          try { rtc.close(); } catch (_) {}
          return;
        }
        rtcRef.current = rtc;
        setRtcReady(true);
        // aplica Mudo/Espera clicados durante a negociação (janela sem rtc)
        try { rtc.setMuted(mutedRef.current || onHoldRef.current); } catch (_) {}
        if (audioRef.current) {
          audioRef.current.srcObject = rtc.remoteStream;
          audioRef.current.muted = onHoldRef.current;
          audioRef.current.play().then(
            () => console.log("[GALLERA-VOICE] saída: <audio>.play() OK", call.callId),
            (e) => console.warn("[GALLERA-VOICE] saída: <audio>.play() bloqueado (autoplay)", e?.name || e)
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[GALLERA-VOICE] saída: falha ao abrir o áudio da chamada", call.callId, err);
        toast.error(voiceErrorMessage(err));
        try { await api.post(`/voice/${call.whatsappId}/hangup`, { callId: call.callId, clientId: clientIdRef.current }); } catch (_) {}
        finish();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cronômetro em chamada
  useEffect(() => {
    if (phase === "inCall" && !timerRef.current) {
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    }
    return undefined;
  }, [phase]);

  // Tom de chamada: RECEBENDO (inbound "ringing") OU CHAMANDO (outbound "dialing" =
  // ringback, igual WhatsApp). Para quando atende (inCall), recusa ou encerra.
  useEffect(() => {
    const shouldRing =
      (phase === "ringing" && call.direction !== "outbound") || phase === "dialing";
    if (shouldRing && !ringStopRef.current) {
      // recebendo cliente = som insistente; ligando (dialing) = ringback suave
      ringStopRef.current = startRingtone(phase === "dialing" ? "ringback" : "incoming");
    } else if (!shouldRing && ringStopRef.current) {
      try { ringStopRef.current(); } catch (_) {}
      ringStopRef.current = null;
    }
    return undefined;
  }, [phase, call.direction]);

  // carrega a lista de microfones (p/ o seletor) — só antes de conectar
  useEffect(() => {
    let alive = true;
    if (phase === "ringing" || phase === "dialing") {
      listMicrophones().then((list) => {
        if (alive) setMics(list);
      });
    }
    return () => { alive = false; };
  }, [phase]);

  const handleSelectMic = useCallback((e) => {
    const id = e.target.value;
    setSelectedMic(id);
    try { localStorage.setItem(MIC_PREF_KEY, id); } catch (_) {}
  }, []);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      try { rtcRef.current?.setMuted(next || onHoldRef.current); } catch (_) {}
      return next;
    });
  }, []);

  // Espera: silencia os DOIS lados (atendente não fala nem ouve; ligação continua aberta)
  const toggleHold = useCallback(() => {
    setOnHold((prev) => {
      const next = !prev;
      onHoldRef.current = next;
      try { rtcRef.current?.setMuted(next || mutedRef.current); } catch (_) {}
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  }, []);

  useEffect(() => () => teardown(), [teardown]);

  // ATENDER = MESMO caminho de mídia do LIGAR (requisito de simetria):
  // answerGalleraInboundCall faz o accept e delega ao MESMO openGalleraCall do
  // outbound (getUserMedia → DataChannel "pcm" → POST /webrtc → AudioWorklets),
  // com o clientId devolvido pelo /answer e o MESMO fallback de 1º áudio remoto.
  // Sem o /webrtc o motor nunca cria o Bridge e o áudio do celular é descartado
  // (era a causa raiz do "atendeu e ficou mudo").
  const handleAnswer = useCallback(async () => {
    if (answeringRef.current || removedRef.current) return; // anti duplo-clique
    answeringRef.current = true;
    answeredByMeRef.current = true;
    // destrava o áudio SOB O GESTO do clique "Atender" (inbound já funcionava por
    // estar na cadeia do clique; mantido por simetria e robustez — idempotente).
    ensureAudioUnlocked();
    // para o toque JÁ no clique e mostra "Conectando…" enquanto a mídia negocia
    setPhase("answering");
    try {
      const { rtc, clientId } = await answerGalleraInboundCall(call.whatsappId, call.callId, {
        micDeviceId: selectedMic || null,
        record,
        // FALLBACK "conectada" (igual ao outbound): 1º áudio remoto garante a fase
        // "em chamada" mesmo se o evento answered do socket não chegar (token).
        onFirstRemoteAudio: () => {
          setPhase("inCall");
        }
      });
      if (clientId) clientIdRef.current = clientId;
      if (removedRef.current) {
        // a chamada acabou (ended) enquanto a mídia negociava — não vaza mic/RTC
        try { rtc.close(); } catch (_) {}
        return;
      }
      rtcRef.current = rtc;
      setRtcReady(true);
      // aplica Mudo/Espera clicados durante a negociação (janela sem rtc)
      try { rtc.setMuted(mutedRef.current || onHoldRef.current); } catch (_) {}
      if (audioRef.current) {
        audioRef.current.srcObject = rtc.remoteStream;
        audioRef.current.muted = onHoldRef.current;
        audioRef.current.play().then(
          () => console.log("[GALLERA-VOICE] atender: <audio>.play() OK", call.callId),
          (e) => console.warn("[GALLERA-VOICE] atender: <audio>.play() bloqueado", e?.name || e)
        );
      }
      setPhase("inCall");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[GALLERA-VOICE] atender: falha", call.callId, err);
      toast.error(voiceErrorMessage(err));
      answeredByMeRef.current = false;
      answeringRef.current = false;
      try { await api.post(`/voice/${call.whatsappId}/hangup`, { callId: call.callId, clientId: clientIdRef.current }); } catch (_) {}
      finish();
    }
  }, [call.whatsappId, call.callId, finish, record, selectedMic]);

  const handleReject = useCallback(async () => {
    try {
      await api.post(`/voice/${call.whatsappId}/reject`, { callId: call.callId });
    } catch (err) {
      toastError(err);
    }
    finish();
  }, [call.whatsappId, call.callId, finish]);

  return (
    <div style={styles.card}>
      <audio ref={audioRef} autoPlay style={{ display: "none" }} />
      <div style={styles.header}>
        {phase === "inCall"
          ? "📞 Em chamada — Gallera Voz"
          : phase === "dialing"
          ? "📞 Chamando… — Gallera Voz"
          : phase === "answering"
          ? "📞 Conectando… — Gallera Voz"
          : "📞 Ligação recebida — Gallera Voz"}
      </div>
      <div style={styles.body}>
        <div style={styles.number}>{call.number || "Desconhecido"}</div>
        <div style={styles.sub}>
          {phase === "dialing"
            ? `chamando ${call.contactName || call.number || ""}…`
            : `conexão ${call.whatsappName || call.whatsappId}`}
        </div>
        {phase === "inCall" && <div style={styles.duration}>{fmt(duration)}</div>}
        {phase === "inCall" && record && (
          <div style={styles.recNotice}>🔴 Esta ligação está sendo gravada</div>
        )}
        {(phase === "ringing" || (phase === "dialing" && !rtcReady)) && mics.length > 1 && (
          <select
            value={selectedMic}
            onChange={handleSelectMic}
            style={styles.micSelect}
            title="Microfone"
          >
            <option value="">🎤 Microfone padrão</option>
            {mics.map((m) => (
              <option key={m.id} value={m.id}>
                🎤 {m.label}
              </option>
            ))}
          </select>
        )}
      </div>
      <div style={styles.actions}>
        {phase === "ringing" ? (
          <>
            <button style={{ ...styles.btn, ...styles.answer }} onClick={handleAnswer}>
              Atender
            </button>
            <button style={{ ...styles.btn, ...styles.reject }} onClick={handleReject}>
              Recusar
            </button>
          </>
        ) : phase === "inCall" || rtcReady ? (
          // Controles completos SEMPRE que a mídia está de pé (rtcReady) ou a chamada
          // conectou — MESMA régua p/ LIGAR e ATENDER (requisito: atender igual ligar).
          // No caminho por token o "answered" pode não chegar e a fase não vira
          // "inCall" — sem isso Mudo/Espera sumiam (visto em produção 2026-07-06).
          <>
            <button
              style={{ ...styles.btn, ...(muted ? styles.ctrlOn : styles.ctrl) }}
              onClick={toggleMute}
              title="Mudo (desliga seu microfone)"
            >
              {muted ? "🔇 Mudo" : "🎤 Mudo"}
            </button>
            <button
              style={{ ...styles.btn, ...(onHold ? styles.ctrlOn : styles.ctrl) }}
              onClick={toggleHold}
              title="Espera (silencia os dois lados)"
            >
              {onHold ? "⏸ Em espera" : "⏸ Espera"}
            </button>
            <button style={{ ...styles.btn, ...styles.hangup }} onClick={handleHangup}>
              Desligar
            </button>
          </>
        ) : (
          <button style={{ ...styles.btn, ...styles.hangup }} onClick={handleHangup}>
            Desligar
          </button>
        )}
      </div>
    </div>
  );
};

const GalleraVoiceCall = () => {
  const { user, socket } = useContext(AuthContext);
  const [enabled, setEnabled] = useState(false);
  const [recordCalls, setRecordCalls] = useState(false);
  // lista de chamadas ativas (várias simultâneas). Cada item:
  // { callId, whatsappId, whatsappName, number, phase, signal }
  const [calls, setCalls] = useState([]);

  // checa o gate (plano + serviço) uma vez
  useEffect(() => {
    let alive = true;
    api
      .get("/voice/enabled")
      .then(({ data }) => {
        if (alive) {
          setEnabled(!!data?.enabled);
          setRecordCalls(!!data?.record);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[GALLERA-VOICE] overlay: GET /voice/enabled falhou (voz desativada nesta sessão):", err?.response?.status || err?.message);
      });
    return () => { alive = false; };
  }, []);

  // DESTRAVA de áudio no 1º gesto da página (autoplay policy). Belt-and-suspenders
  // do fix do OUTBOUND: garante que, quando o card outbound montar e abrir o áudio
  // num useEffect (fora do gesto do clique "Ligar"), o documento JÁ esteja
  // audio-allowed e o AudioContext da chamada entre em "running". Só quando a voz
  // está habilitada; dispara uma vez e se remove. Não toca no inbound.
  useEffect(() => {
    if (!enabled) return undefined;
    const unlock = () => {
      ensureAudioUnlocked();
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, [enabled]);

  const removeCall = useCallback((callId) => {
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
  }, []);

  // marca um sinal externo (answered/claimed/ended) no item da lista; o CallCard reage
  const signalCall = useCallback((callId, signal) => {
    setCalls((prev) =>
      prev.map((c) => (c.callId === callId ? { ...c, signal } : c))
    );
  }, []);

  // OUTBOUND: o botão "Ligar (Gallera Voz)" no ticket dispara um window event
  // (galleraVoice:startCall) com { whatsappId, callId, number, contactName,
  // whatsappName }. Aqui inserimos o card em modo "dialing" — mesma lista das
  // simultâneas, mesmo componente. Assim o botão do ticket não precisa conhecer
  // o overlay (que é montado globalmente no provider).
  useEffect(() => {
    if (!enabled) return undefined;
    const onStart = (e) => {
      const d = (e && e.detail) || {};
      if (!d.callId || !d.whatsappId) return;
      setCalls((prev) => {
        if (prev.some((c) => c.callId === d.callId)) return prev; // dedup
        return [
          ...prev,
          {
            callId: d.callId,
            clientId: d.clientId, // owner da saída (gv-<user>-out-<nonce>) p/ webrtc/hangup
            whatsappId: d.whatsappId,
            whatsappName: d.whatsappName,
            number: d.number,
            contactName: d.contactName,
            direction: "outbound",
            phase: "dialing",
            signal: null
          }
        ];
      });
    };
    window.addEventListener("galleraVoice:startCall", onStart);
    return () => window.removeEventListener("galleraVoice:startCall", onStart);
  }, [enabled]);

  // listener do socket
  useEffect(() => {
    if (!enabled || !socket || !user?.companyId) return undefined;
    const companyId = user.companyId;

    const onCall = (data) => {
      if (!data || !data.callId) return;
      // eslint-disable-next-line no-console
      console.log("[GALLERA-VOICE] evento de chamada (socket):", data.action, data.callId, data.whatsappId || "");
      if (data.action === "offer") {
        // Roteamento: se a chamada tem um atendente-dono (targetUserId, do ticket
        // aberto), toca só p/ ele e p/ admins; os demais nem veem o card. Se não há
        // dono (targetUserId nulo), toca p/ todos (broadcast, primeiro que atende).
        const isAdmin = user?.profile === "admin" || user?.super;
        if (data.targetUserId && !isAdmin && Number(data.targetUserId) !== Number(user?.id)) {
          return;
        }
        // Roteamento por CONEXÃO: se a conexão tem lista de usuários (allowedUserIds,
        // configurada no cadastro da conexão), só toca p/ quem está na lista (+ admins).
        // Lista vazia = comportamento padrão (todos). Não se aplica quando há dono do
        // ticket (targetUserId acima tem precedência).
        const allowed = Array.isArray(data.allowedUserIds)
          ? data.allowedUserIds.map(Number)
          : [];
        if (
          !data.targetUserId &&
          allowed.length > 0 &&
          !isAdmin &&
          !allowed.includes(Number(user?.id))
        ) {
          return;
        }
        setCalls((prev) => {
          if (prev.some((c) => c.callId === data.callId)) return prev; // dedup
          return [
            ...prev,
            {
              callId: data.callId,
              whatsappId: data.whatsappId,
              whatsappName: data.whatsappName,
              number: data.name || data.number || String(data.from || "").split("@")[0],
              contactName: data.name || "",
              phase: "ringing",
              signal: null
            }
          ];
        });
      } else if (data.action === "answered") {
        signalCall(data.callId, "answered");
      } else if (data.action === "claimed") {
        // outro atendente pegou; fecha o card de quem NÃO atendeu.
        // (o próprio atendente que aceitou já está com o card em inCall e o
        //  finish só dispara no ended — aqui usamos owner p/ não fechar o dele)
        const myOwnerPrefix = `gv-${user?.id}-`;
        const isMine = String(data.owner || "").startsWith(myOwnerPrefix);
        if (!isMine) signalCall(data.callId, "claimed");
      } else if (data.action === "ended") {
        signalCall(data.callId, "ended");
      }
    };

    socket.on(`company-${companyId}-call`, onCall);
    return () => {
      socket.off(`company-${companyId}-call`, onCall);
    };
  }, [enabled, socket, user, signalCall]);

  if (!enabled) return null;

  return (
    <div style={styles.stack}>
      {calls.map((call) => (
        <CallCard key={call.callId} call={call} onRemove={removeCall} record={recordCalls} />
      ))}
    </div>
  );
};

export default GalleraVoiceCall;
